/**
 * Helpers for the redirect-based sign-in handoff. Non-super-admin users
 * authenticate elsewhere and land on the control panel with a JWT in the
 * URL hash:
 *
 *     https://control-panel.example.com/#token=eyJhbGciOi...
 *
 * The token is read once on first load, then immediately stripped from
 * the URL so it never lingers in the address bar, browser history, or
 * referrer headers.
 */

/** Hash param carrying the handoff JWT (`/#token=<jwt>`). */
export const HASH_TOKEN_PARAM = 'token'

/** Normalize a `window.location.hash` value into raw query-string form. */
function normalizeHash(hash: string): string {
  // Drop the leading '#', plus an optional '/' or '?' (e.g. '#/?token=…').
  return hash.replace(/^#/, '').replace(/^[/?]/, '')
}

/** Extract the handoff token from a location hash, or `null` if absent. */
export function getTokenFromHash(hash: string): string | null {
  const raw = normalizeHash(hash)
  if (!raw) return null
  const token = new URLSearchParams(raw).get(HASH_TOKEN_PARAM)
  return token && token.trim() ? token.trim() : null
}

/**
 * Remove the token from the current URL without a navigation/reload,
 * preserving the path, query string, and any other hash params.
 */
export function clearTokenFromUrl(): void {
  const params = new URLSearchParams(normalizeHash(window.location.hash))
  params.delete(HASH_TOKEN_PARAM)
  const rest = params.toString()
  const { pathname, search } = window.location
  const nextUrl = `${pathname}${search}${rest ? `#${rest}` : ''}`
  window.history.replaceState(window.history.state, '', nextUrl)
}

let captured = false
let capturedToken: string | null = null

/**
 * Read the handoff token from the URL hash exactly once per page load
 * and strip it from the URL. Subsequent calls return the same captured
 * value — safe against React StrictMode's double-invocation of effects
 * and lazy state initializers.
 */
export function captureHashTokenOnce(): string | null {
  if (captured) return capturedToken
  captured = true
  capturedToken = getTokenFromHash(window.location.hash)
  if (capturedToken) clearTokenFromUrl()
  return capturedToken
}
